import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { post } from '@/lib/api/client';

/**
 * Transport for the page-visit analytics endpoints.
 *
 * The four endpoints take one body: a range, an optional app and store, and
 * the subjects to report on. A subject is whose visits are counted — one
 * account, or everybody matching a set of filters — and each is answered on
 * its own under the key it was sent with, never summed with the others.
 */

/**
 * How many subjects the page compares at once.
 *
 * The API takes six. The limit here is the chart palette: it has four series
 * colours because a fifth cannot be told apart from them under colour-vision
 * deficiency, and a subject without a colour of its own is a line nobody can
 * follow from the legend to the plot.
 */
export const MAX_SUBJECTS = 4;

export interface PersonSubject {
  kind: 'person';
  key: string;
  /** Index into the series colours. Kept for the subject's life, so removing one never repaints the rest. */
  slot: number;
  userId: string;
  name: string;
  email: string | null;
}

export interface GroupFilters {
  /** 0 male, 1 female, 2 not said. */
  gender?: number;
  ageMin?: number;
  ageMax?: number;
  language?: number;
  guest?: boolean;
  role?: string;
  /** Encoded store id. The name rides along so the label needs no store list. */
  favoriteStoreId?: string;
  favoriteStoreName?: string;
  /** Account created on or after / on or before, as picked `yyyy-MM-dd`. */
  registeredFrom?: string;
  registeredTo?: string;
}

/** A group with no filter set, which is everybody. */
export function isEveryone(subject: Subject) {
  return (
    subject.kind === 'group' &&
    subject.gender === undefined &&
    subject.ageMin === undefined &&
    subject.ageMax === undefined &&
    subject.language === undefined &&
    subject.guest === undefined &&
    !subject.role &&
    !subject.favoriteStoreId &&
    !subject.registeredFrom &&
    !subject.registeredTo
  );
}

/** Everybody the filters describe. With none set, everybody. */
export interface GroupSubject extends GroupFilters {
  kind: 'group';
  key: string;
  slot: number;
}

export type Subject = PersonSubject | GroupSubject;

export interface UsageFilters {
  /** Picked `yyyy-MM-dd`, read as days on the reader's own calendar. */
  start: string;
  end: string;
  /** One client app, or null for all of them. */
  domain: string | null;
}

export interface PageVisitTotalsDto {
  visits: number;
  activeUsers: number;
  /** Distinct browser tabs. Visits recorded without a session id are not in here. */
  sessions: number;
  lastVisitedOn: string | null;
}

export interface PageVisitDayDto extends PageVisitTotalsDto {
  /** Midnight of the reader's day, zoneless. Days with no visits are absent. */
  date: string;
}

export interface SubjectSummaryDto {
  key: string;
  /** Accounts the subject describes, visited or not. */
  members: number;
  totals: PageVisitTotalsDto;
  /** The same length of time just before the range. Null once that reaches past what is kept. */
  previous: PageVisitTotalsDto | null;
  days: PageVisitDayDto[];
}

export interface PageCountDto {
  page: string;
  domain: string;
  visits: number;
  users: number;
  /** In the same length of time just before the range. Null once that is no longer kept. */
  previousVisits: number | null;
}

export interface SubjectPagesDto {
  key: string;
  /** Every visit in the range, whatever the page: the denominator of a page's share. */
  totalVisits: number;
  previousTotalVisits: number | null;
  /**
   * Most visited first, then pages that were popular in the earlier span and
   * have dropped out since — those can carry zero visits.
   */
  pages: PageCountDto[];
}

export type BreakdownDimension =
  | 'gender'
  | 'age'
  | 'language'
  | 'domain'
  | 'business';

export interface SegmentDto {
  /** An enum number, an age band, a domain or an encoded store id, as text. */
  value: string;
  /** Set for a store. */
  name: string | null;
  visits: number;
  activeUsers: number;
}

export interface SubjectBreakdownDto {
  key: string;
  segments: SegmentDto[];
}

export interface CohortCellDto {
  /** The Sunday the cohort was first seen in the week of, zoneless. */
  cohortWeek: string;
  activeWeek: string;
  users: number;
}

export interface SubjectRetentionDto {
  key: string;
  cohorts: CohortCellDto[];
  /** Active just before the range and not in it. Null once that span is no longer kept. */
  lapsed: number | null;
}

export interface FlowCountDto {
  /** Null in a next-page list: the session ended on the page asked about. */
  page: string | null;
  domain: string | null;
  count: number;
}

export interface DepthBucketDto {
  /** '1', '2-5', '6-10' or '11+'. */
  pages: string;
  sessions: number;
}

export interface SubjectFlowsDto {
  key: string;
  /** Sessions counted: the denominator for entries, exits and depth. */
  sessions: number;
  entries: FlowCountDto[];
  exits: FlowCountDto[];
  depth: DepthBucketDto[];
  /** Null when no page was asked about. */
  next: FlowCountDto[] | null;
  /** Visits to the page asked about: the denominator for `next`. */
  fromVisits: number | null;
}

export interface HeatmapCellDto {
  /** 0 is Sunday. */
  dayOfWeek: number;
  hour: number;
  visits: number;
}

export interface SubjectHeatmapDto {
  key: string;
  cells: HeatmapCellDto[];
}

export interface MemberDto {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  visits: number;
  lastVisitedOn: string;
}

export const MEMBERS_PAGE_SIZE = 50;

/**
 * A picked day as the UTC instant it starts or ends at for the reader.
 *
 * Not `toApiDate` from `lib/store-time`: that is the store's wall clock, and
 * this report is bucketed in the reader's zone — the offset below is sent with
 * the range so the API cuts its days at the same midnights these bounds do.
 */
function toUtcInstant(day: string, endOfDay: boolean) {
  const local = new Date(`${day}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return local.toISOString().slice(0, 23);
}

function subjectBody(subject: Subject) {
  if (subject.kind === 'person') {
    return { Key: subject.key, UserId: subject.userId };
  }
  return {
    Key: subject.key,
    Gender: subject.gender ?? null,
    AgeMin: subject.ageMin ?? null,
    AgeMax: subject.ageMax ?? null,
    PreferredLanguage: subject.language ?? null,
    IsGuestUser: subject.guest ?? null,
    Role: subject.role ?? null,
    FavoriteStoreId: subject.favoriteStoreId ?? null,
    RegisteredFrom: subject.registeredFrom
      ? toUtcInstant(subject.registeredFrom, false)
      : null,
    RegisteredTo: subject.registeredTo
      ? toUtcInstant(subject.registeredTo, true)
      : null,
  };
}

function requestBody(filters: UsageFilters, subjects: Subject[]) {
  return {
    StartDate: toUtcInstant(filters.start, false),
    EndDate: toUtcInstant(filters.end, true),
    Domain: filters.domain,
    BusinessId: null,
    // JS reports minutes *behind* UTC; the API wants minutes to add to it.
    TimeZoneOffsetMinutes: -new Date().getTimezoneOffset(),
    Subjects: subjects.map(subjectBody),
  };
}

function usable(filters: UsageFilters, subjects: Subject[]) {
  return (
    subjects.length > 0 &&
    Boolean(filters.start) &&
    Boolean(filters.end) &&
    filters.start <= filters.end
  );
}

/**
 * The three reads share one shape, so they share one hook.
 *
 * The body is the query key: a subject added, a colour slot aside, is a
 * different question. `keepPreviousData` holds the panels on screen while the
 * new answer loads, so adding a fourth subject dims three lines rather than
 * blanking them.
 */
function useUsageQuery<T>(
  endpoint:
    | 'summary'
    | 'pages'
    | 'heatmap'
    | 'breakdown'
    | 'retention'
    | 'flows',
  filters: UsageFilters,
  subjects: Subject[],
  extra?: Record<string, unknown>
) {
  const body = { ...requestBody(filters, subjects), ...extra };

  return useQuery({
    queryKey: ['usage-analytics', endpoint, body],
    enabled: usable(filters, subjects),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // OkPageData nests the array under `results` rather than returning it.
      const data = await post<{ results: T[] }>(
        `analytics/pagevisits/${endpoint}`,
        body
      );
      return data?.results ?? [];
    },
  });
}

export function useUsageSummary(filters: UsageFilters, subjects: Subject[]) {
  return useUsageQuery<SubjectSummaryDto>('summary', filters, subjects);
}

export function useUsagePages(
  filters: UsageFilters,
  subjects: Subject[],
  top: number
) {
  return useUsageQuery<SubjectPagesDto>('pages', filters, subjects, { Top: top });
}

export function useUsageHeatmap(filters: UsageFilters, subjects: Subject[]) {
  return useUsageQuery<SubjectHeatmapDto>('heatmap', filters, subjects);
}

export function useUsageBreakdown(
  filters: UsageFilters,
  subjects: Subject[],
  dimension: BreakdownDimension
) {
  return useUsageQuery<SubjectBreakdownDto>('breakdown', filters, subjects, {
    Dimension: dimension,
  });
}

export function useUsageRetention(filters: UsageFilters, subjects: Subject[]) {
  return useUsageQuery<SubjectRetentionDto>('retention', filters, subjects);
}

/**
 * Where sessions start and end, how long they run, and — when `from` names a
 * page — what is opened straight after it.
 */
export function useUsageFlows(
  filters: UsageFilters,
  subjects: Subject[],
  from: { domain: string; page: string } | null
) {
  return useUsageQuery<SubjectFlowsDto>('flows', filters, subjects, {
    FromPage: from?.page ?? null,
    FromDomain: from?.domain ?? null,
  });
}

/**
 * Which accounts a list is of.
 *
 * `members` visited in the range. `lapsed` visited in the same length of time
 * just before it and have not since — their counts describe that earlier span,
 * which is the only activity a lapsed account has to show.
 */
export type MemberList = 'members' | 'lapsed';

/**
 * The accounts behind one group, busiest first.
 *
 * Paged the way the visit history is: the list carries no total, and a short
 * page is how its end announces itself.
 */
export function useUsageMembers(
  filters: UsageFilters,
  subject: Subject | null,
  list: MemberList
) {
  const body = subject ? requestBody(filters, [subject]) : null;

  return useInfiniteQuery({
    queryKey: ['usage-analytics', list, body],
    enabled: Boolean(subject) && usable(filters, subject ? [subject] : []),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const data = await post<{ results: MemberDto[] }>(
        `analytics/pagevisits/${list}`,
        { ...body, Skip: pageParam, Take: MEMBERS_PAGE_SIZE }
      );
      return data?.results ?? [];
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < MEMBERS_PAGE_SIZE
        ? undefined
        : allPages.reduce((total, page) => total + page.length, 0),
  });
}

/** The most one request returns, and the most an export will gather. */
const EXPORT_PAGE_SIZE = 200;
export const EXPORT_LIMIT = 5000;

/**
 * Every account in a list, for an export.
 *
 * Walked a page at a time rather than asked for at once: the endpoint caps a
 * request at 200 rows on purpose, and an export is not a reason to lift that.
 * Stops at `EXPORT_LIMIT`, which the caller reports rather than hides.
 */
export async function fetchAllMembers(
  filters: UsageFilters,
  subject: Subject,
  list: MemberList
) {
  const body = requestBody(filters, [subject]);
  const rows: MemberDto[] = [];

  while (rows.length < EXPORT_LIMIT) {
    const data = await post<{ results: MemberDto[] }>(
      `analytics/pagevisits/${list}`,
      { ...body, Skip: rows.length, Take: EXPORT_PAGE_SIZE }
    );
    const page = data?.results ?? [];
    rows.push(...page);
    if (page.length < EXPORT_PAGE_SIZE) break;
  }

  return rows;
}
